import React, { useCallback, useEffect, useState } from "react";

//QUERIES
//
import { useQueryClient } from "@tanstack/react-query";

//TYPESCRIPT
//

//ICONS
//
import LinkInputIcon from "../../Icons/LinkInputIcon";

import { Hotspot, Scene, TourData } from "../../../Types";
import { UseGetTour, UseSaveTour } from "../../../api/calls/Queries";

import { useForm, SubmitHandler } from "react-hook-form";
import OtherHotspots from "./OtherHotspots";
import {
  useCurrentSceneStore,
  useEditingElementStore,
} from "../../../api/Zustand";
import { EditPencil, Trashbin } from "../../Icons/gui-elements-icons";
import { getLanguageFile } from "../../common/GetLanguageFile";

function LinkEditing({ id, tourID }: { id: string; tourID: number }) {
  const { setEditingElem } = useEditingElementStore();

  const { currentSceneID } = useCurrentSceneStore();

  const queryClient = useQueryClient();
  const [currentLink, setCurrentLink] = useState<Hotspot>();

  const { mutateAsync: saveTour, isPending: isSaving } = UseSaveTour();

  const { data: tourData } = UseGetTour(tourID);

  const { register, handleSubmit, formState, watch, reset } =
    useForm<Hotspot>();

  useEffect(() => {
    if (tourData) {
      let temp = tourData?.scenes
        ?.find((one: Scene) => one.id === currentSceneID)
        ?.hotspots.find((one: Hotspot) => one.id === id);

      setCurrentLink(temp);
    }
  }, [currentSceneID, id, tourData]);

  const onTargetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentLink({ ...currentLink, target: e.target.value } as Hotspot);
  };

  const onTitlechange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentLink((current) => {
        return {
          ...current,
          title: e.target.value,
        } as Hotspot;
      });
    },
    []
  );

  const handleLinkUpdate = async () => {
    const newTourData = queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData?.scenes.map((sc: Scene) =>
        sc.id === currentSceneID
          ? {
              ...sc,
              hotspots: sc.hotspots.map((hs: Hotspot) =>
                hs.id === id
                  ? {
                      ...currentLink,
                      EDIT_POS: undefined,
                      isNew: undefined,
                    }
                  : hs
              ),
            }
          : sc
      ),
    });
    saveTour(newTourData as TourData);
  };

  //
  const handlePosEdit = async () => {
    if (currentLink) {
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: [
          ...tourData!.scenes.map((sc: Scene) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  hotspots: sc.hotspots.map((hs: Hotspot) =>
                    hs.id === currentLink.id
                      ? {
                          ...currentLink,
                          EDIT_POS: currentLink.EDIT_POS ? undefined : true,
                        }
                      : hs
                  ),
                }
              : sc
          ),
        ],
      });
    }
  };

  const handleDelete = async () => {
    const tempSceneSpots = tourData!.scenes
      .find((one) => one.id === currentSceneID)!
      .hotspots.filter((spot) => spot.id !== currentLink!.id);

    const newTourData = queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData!.scenes.map((scene) =>
        scene.id === currentSceneID
          ? { ...scene, hotspots: tempSceneSpots }
          : scene
      ),
    });

    setEditingElem("scene", currentSceneID);
    saveTour(newTourData as TourData);
  };

  const handleForm: SubmitHandler<Hotspot> = (data) => {
    return data;
  };

  return (
    <div
      className="right-side-panel-editor"
      style={{
        maxHeight: "100%",
        overflow: "hidden",
        display: "flex",
      }}
    >
      {currentLink && (
        <div>
          <h3>LINK</h3>

          <form onSubmit={handleSubmit(handleForm)}></form>

          <label>
            {getLanguageFile("right_panel.title")}
            <input
              type="text"
              value={currentLink.title}
              placeholder={getLanguageFile(
                "right_panel.title_placeholder_link"
              )}
              onChange={onTitlechange}
            />
          </label>

          <label
            htmlFor="url"
            style={{
              marginTop: "10px",
            }}
          >
            {getLanguageFile("right_panel.link")}

            <div className="url-wrapper">
              <LinkInputIcon
                width="1em"
                height="1em"
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "10px",
                  transform: "translateY(-50%)",
                }}
              />

              <input
                type="url"
                id="url"
                {...register("target", { required: true })}
                value={currentLink.target}
                onChange={(e) => onTargetChange(e)}
                placeholder={getLanguageFile("right_panel.url_placeholder")}
                pattern="https://*"
              />
            </div>
          </label>

          <button
            id="edit"
            style={{ width: "100%", marginTop: "10px" }}
            className={currentLink.EDIT_POS ? "active" : undefined}
            onClick={handlePosEdit}
          >
            {currentLink.EDIT_POS ? (
              getLanguageFile("right_panel.save_position")
            ) : (
              <>
                {getLanguageFile("right_panel.edit_position")} <EditPencil />
              </>
            )}
          </button>

          <div style={{ display: "flex" }}>
            <button
              id="save"
              onClick={handleLinkUpdate}
              disabled={isSaving}
              style={{ flex: 1 }}
            >
              {currentLink.isNew
                ? getLanguageFile("generics.create")
                : isSaving
                ? getLanguageFile("generics.saving")
                : getLanguageFile("generics.save")}
            </button>

            <button id="delete" onClick={handleDelete} style={{ flex: 1 }}>
              {currentLink.isNew ? (
                getLanguageFile("generics.cancel")
              ) : (
                <>
                  {getLanguageFile("generics.delete")} <Trashbin />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {currentLink && (
        <OtherHotspots
          currentHotspot={currentLink}
          icon={<LinkInputIcon width="1em" height="1em" />}
          tourData={tourData!}
          type="link"
        ></OtherHotspots>
      )}
    </div>
  );
}

export default LinkEditing;
